import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Mail, Loader2, CheckCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error('Please enter your email address / 請輸入您的電子郵件');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/forgot-password`, { email });
      setSubmitted(true);
      toast.success('Check your email for reset instructions / 請檢查您的電子郵件以獲取重設說明');
    } catch (error) {
      // Even if error, show generic message for security
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-fac-dark flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Back to Login Link */}
        <Link to="/login" className="flex items-center gap-2 text-fac-green hover:text-fac-green-dim mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          <span className="font-chinese">Back to Login / 返回登入</span>
        </Link>

        <Card className="bg-fac-card border-border">
          <CardHeader className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-fac-green/10 flex items-center justify-center">
              {submitted ? (
                <CheckCircle className="w-8 h-8 text-fac-green" />
              ) : (
                <Mail className="w-8 h-8 text-fac-green" />
              )}
            </div>
            <CardTitle className="text-2xl font-heading text-white uppercase">
              {submitted ? 'Email Sent' : 'Forgot Password'}
            </CardTitle>
            <CardDescription className="text-muted-foreground font-chinese">
              {submitted 
                ? '已發送重設郵件 / Reset email sent'
                : '忘記密碼 / Enter your email to reset'
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            {submitted ? (
              <div className="space-y-4">
                <div className="p-4 bg-fac-green/10 rounded-lg text-center">
                  <p className="text-white mb-2">
                    If an account exists with this email, you will receive password reset instructions.
                  </p>
                  <p className="text-muted-foreground font-chinese text-sm">
                    如果此電子郵件已註冊帳戶，您將收到密碼重設說明。
                  </p>
                </div>
                
                <div className="text-center space-y-3">
                  <p className="text-sm text-muted-foreground font-chinese">
                    Didn't receive the email? Check your spam folder. / 沒收到郵件？請檢查垃圾郵件資料夾。
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => setSubmitted(false)}
                    className="border-fac-green text-fac-green hover:bg-fac-green/10"
                  >
                    Try another email / 嘗試其他郵件
                  </Button>
                </div>

                <div className="pt-4 border-t border-border text-center">
                  <Link to="/login">
                    <Button className="bg-fac-green text-black font-bold">
                      Return to Login / 返回登入
                    </Button>
                  </Link>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-white font-chinese">
                    Email Address / 電子郵件
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="bg-fac-dark border-border text-white"
                    data-testid="forgot-email-input"
                    required
                  />
                </div>

                <p className="text-sm text-muted-foreground font-chinese">
                  Enter the email address associated with your account and we'll send you a link to reset your password.
                  <br />
                  輸入與您帳戶關聯的電子郵件地址，我們將向您發送重設密碼的連結。
                </p>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-fac-green text-black font-bold uppercase tracking-wider hover:bg-fac-green-dim"
                  data-testid="forgot-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending... / 發送中...
                    </>
                  ) : (
                    'Send Reset Link / 發送重設連結'
                  )}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
